import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';
import {
  Box,
  Grid, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import React from 'react';
import {
  ContentMediaView,
  ErrorMessage,
  PrimaryLiteButton,
  PrimaryLoadingButton,
  PrimaryTextField,
  PrimarySelect,
  FileDragUploader,
  YouTubePreview,
} from 'components';
import { CharacterCountDisplay } from 'components/molecules/primary-text-field';
import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import { extensionToType } from 'utils/file-uploader';
import { validateYouTubeUrl } from 'utils/youtube-utils';
import { BlockType } from '@surge-global-engineering/form-builder-v2';
import { UrlType } from 'pages/media-library/interface';
import { ICreateContentModalForm } from '../../../interface';
import { addContentFieldConfigs } from '../validation/add-content-modal-form-validation';

interface ContentModalFormProps {
  onSubmit: SubmitHandler<any>;
  closeModal: () => void;
  loading:boolean;
  blockType?: BlockType;
  scenarios?: UploadScenarios
}

const ContentModalForm = ({
  onSubmit,
  closeModal, loading, blockType, scenarios,
}: ContentModalFormProps) => {
  const {
    control,
    handleSubmit, setValue, setError, clearErrors, watch, reset,
    formState: { errors },
  } = useFormContext<ICreateContentModalForm>();

  const [scenario, setScenario] = React.useState<UploadScenarios>(UploadScenarios.expertContent);

  const watchIsExternalFile = watch('isExternalFile');
  const watchContentUrl = watch('contentUrl');
  const watchFileCategory = watch('fileCategory', '');
  const watchFileType = watch('fileType', '');
  const [uploadButtonDisabled, setUploadButtonDisabled] = React.useState(false);
  const [isValidUrl, setIsValidUrl] = React.useState(false);
  const [urlType, setUrlType] = React.useState<UrlType>(UrlType.PUBLIC_URL);

  const getScenario = (blockTypeInput?: BlockType): UploadScenarios => (
    blockTypeInput === BlockType.TEXT
      ? UploadScenarios.textBlock
      : UploadScenarios.expertContent
  );

  React.useEffect(() => {
    if (blockType) {
      const updatedScenario = getScenario(blockType);
      setScenario(updatedScenario);
    }
  }, [blockType]);

  React.useEffect(() => {
    if (scenarios) {
      setScenario(scenarios);
    }
  });

  const handleUpload = (res:UploadResponse) => {
    setValue('contentUrl', res.url);
    setValue('fileType', res.extension.toUpperCase());
    setValue('fileCategory', res.category ?? '');
    setValue('fileName', res.name);
    if (res.statusCode !== 201) {
      setError('contentUrl', { type: 'custom', message: res.message });
      setValue('contentUrl', '');
    } else {
      setValue('contentUrl', res.url);
    }
  };

  const getMediaDataFromUrl = (url:string) => {
    const urlParts = url.split('.');
    if (urlParts.length < 2) {
      setIsValidUrl(false);
      setUploadButtonDisabled(true);
      return;
    }

    const extension = urlParts[urlParts.length - 1].toLowerCase();
    const fileType = `.${extension}`;
    const fileName = url.split('/').pop();

    if (fileType && fileName && extensionToType.get(fileType)) {
      const fileCategory = extensionToType.get(fileType) as string;
      setValue('fileType', extension.toUpperCase());
      setValue('fileCategory', fileCategory);
      setValue('fileName', fileName);
      setIsValidUrl(true);
      setUploadButtonDisabled(false);
      clearErrors('contentUrl');
      return;
    }
    setIsValidUrl(false);
    setUploadButtonDisabled(true);
  };

  const getYouTubeDataFromUrl = (url: string) => {
    clearErrors('contentUrl');

    const urlInfo = validateYouTubeUrl(url);
    if (urlInfo.isValid && urlInfo.videoId) {
      setValue('fileType', 'MP4');
      setValue('fileCategory', 'video');
      setValue('fileName', `YouTube Video - ${urlInfo.videoId}`);
      setValue('urlType', UrlType.YOUTUBE_LINK);
      setIsValidUrl(true);
      setUploadButtonDisabled(false);
    } else {
      setError('contentUrl', { type: 'custom', message: 'Invalid YouTube URL. Please enter a valid YouTube URL.' });
      setIsValidUrl(false);
      setUploadButtonDisabled(true);
    }
  };
  const handleUrlModeChange = (event: any) => {
    clearErrors();
    const isExternal = event.target.value === 'url';
    reset({
      contentUrl: '',
      fileType: '',
      fileCategory: '',
      fileName: '',
      isExternalFile: isExternal,
      urlType,
      path: '/root',
    });
  };

  const handleUrlTypeChange = (event: any) => {
    const newUrlType = event.target.value as UrlType;
    setUrlType(newUrlType);
    setValue('urlType', newUrlType);

    clearErrors();
    setValue('contentUrl', '');
    setValue('fileType', '');
    setValue('fileCategory', '');
    setValue('fileName', '');
    setValue('path', '/root');
    setIsValidUrl(false);
    setUploadButtonDisabled(true);
  };

  const handleForm = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit(onSubmit)();
  };

  React.useEffect(() => {
    if (watchIsExternalFile && watchContentUrl) {
      if (urlType === UrlType.YOUTUBE_LINK) {
        getYouTubeDataFromUrl(watchContentUrl);
      } else if (urlType === UrlType.PUBLIC_URL) {
        getMediaDataFromUrl(watchContentUrl);
      }
    } else if (watchIsExternalFile && !watchContentUrl) {
      clearErrors('contentUrl');
      setIsValidUrl(false);
      setUploadButtonDisabled(true);
    }
  }, [watchContentUrl, urlType, watchIsExternalFile]);

  return (
    <form onSubmit={handleForm}>

      <Typography variant="h4" className="form-item">
        Upload media
      </Typography>
      <div className="form-item">
        <ToggleButtonGroup
          value={watchIsExternalFile ? 'url' : 'file'}
          fullWidth
          exclusive
          onChange={handleUrlModeChange}
          aria-label="Platform"
        >
          <ToggleButton sx={{ color: '#9D8336' }} value="file">Click to upload a file</ToggleButton>
          <ToggleButton sx={{ color: '#9D8336' }} value="url">Click to enter a url</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {watchIsExternalFile && (
        <div className="form-item">
          <PrimarySelect
            label="URL Type"
            value={urlType}
            onChange={handleUrlTypeChange}
            options={[
              { id: 1, value: UrlType.PUBLIC_URL, text: 'Public URL (Direct media file link)' },
              { id: 2, value: UrlType.YOUTUBE_LINK, text: 'YouTube URL' },
            ]}
          />
        </div>
      )}
      {watchIsExternalFile ? (
        <div>
          {watchContentUrl && isValidUrl && (
            <div className="file-upload-content-media-view d-flex align-items-center justify-content-center mb-2">
              {urlType === UrlType.YOUTUBE_LINK ? (
                <YouTubePreview
                  url={watchContentUrl}
                  height="200px"
                  width="100%"
                  showControls={false}
                  style={{
                    borderRadius: '6px',
                    overflow: 'hidden',
                  }}
                />
              ) : (
                <ContentMediaView url={watchContentUrl} mediaType={watchFileCategory ?? ''} fileType={watchFileType} />
              )}
            </div>
          )}
          <Controller
            control={control}
            name="contentUrl"
            render={({ field }) => (
              <PrimaryTextField
                {...field}
                sx={{ mt: 0 }}
                label="Media URL"
                placeholder={urlType === UrlType.YOUTUBE_LINK ? 'YouTube URL (e.g., https://www.youtube.com/watch?v=...)' : 'Media file URL'}
              />
            )}
          />
          <div className="mb-3 mt-1">
            {errors && <ErrorMessage>{errors?.contentUrl?.message}</ErrorMessage>}
            {!isValidUrl && watchContentUrl && (
              <ErrorMessage>URL is invalid</ErrorMessage>
            )}
          </div>
        </div>
      ) : (
        <div className="form-item">
          <Controller
            control={control}
            name="contentUrl"
            render={({ field: _field }) => (
              <FileDragUploader
                textLabel="click or drag a file to this area to upload"
                handleFile={handleUpload}
                scenario={scenario}
                currentContentUrl=""
                uploadAnotherText="Upload another media"
                currentMediaType=""
              />
            )}
          />
          <ErrorMessage>{errors?.contentUrl?.message}</ErrorMessage>
        </div>
      )}

      <div className="form-item">
        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <>
              <PrimaryTextField {...field} label="Title " />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{errors?.title?.message}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={addContentFieldConfigs.title} />
              </Box>
            </>
          )}
        />
      </div>
      <div className="form-item">
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <>
              <PrimaryTextField multiline {...field} label="Description " />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{errors?.description?.message}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={addContentFieldConfigs.description} />
              </Box>
            </>
          )}
        />
      </div>
      <Grid container spacing={3}>
        <Grid item xs>
          <PrimaryLiteButton fullWidth onClick={closeModal}>Cancel</PrimaryLiteButton>
        </Grid>
        <Grid item xs>
          <PrimaryLoadingButton loading={loading} fullWidth type="submit" disabled={uploadButtonDisabled && (watchContentUrl !== '')}>
            Upload
          </PrimaryLoadingButton>
        </Grid>

      </Grid>
    </form>
  );
};

ContentModalForm.defaultProps = {
  blockType: undefined,
  scenarios: undefined,
};

export default ContentModalForm;
